import { EHeaderStateActionType } from './types';
import { IHeaderData } from '../../headerData';
import { getHeaderData } from '../../services/getHeaderData';
import { getMyHomeIsExists } from '../../services/getMyHomeIsExists';
import { getNotificationsCount } from '../../services/getNotificationsCount';
import { TThunkAction } from '../../types/redux';
import { getLocationIdFromCookie } from '../../utils/cookie';
import { actionGenerator } from '../../utils/redux/actionGenerator';
import { isSameAuthenticatedSession, isSameAuthenticatedUser } from '../../utils/userSession';
import { invalidateUserFavorites, resetUserFavorites } from '../favorites';
import { loadUnreadMessages, resetUnreadMessages } from '../messages';
import { updateIsMyHomeExist } from '../myHome';
import { updateNotificationsCount } from '../notifications/updateCount';

const latestRefreshIds = new WeakMap<object, number>();
let nextRefreshId = 0;

export const updateHeaderData = actionGenerator<EHeaderStateActionType.Update, IHeaderData>(
  EHeaderStateActionType.Update,
);

export const updateState = (): TThunkAction<Promise<void>> => async (dispatch, getState, context) => {
  const refreshId = ++nextRefreshId;
  const requestedSession = getState().user;
  latestRefreshIds.set(dispatch, refreshId);

  try {
    const [headerData, notificationsCount, myHomeIsExist] = await Promise.all([
      getHeaderData(context, { subdomain: context.custom.subdomain, locationId: getLocationIdFromCookie(context) }),
      getNotificationsCount(context),
      getMyHomeIsExists(context),
    ]);

    const currentUser = getState().user;

    if (latestRefreshIds.get(dispatch) !== refreshId || !isSameAuthenticatedSession(requestedSession, currentUser)) {
      return;
    }

    const isSameUser = isSameAuthenticatedUser(currentUser, headerData.user);

    dispatch(isSameUser ? invalidateUserFavorites() : resetUserFavorites());
    if (!isSameUser) {
      dispatch(resetUnreadMessages());
    }
    dispatch(updateHeaderData(headerData));
    dispatch(updateNotificationsCount(notificationsCount));
    dispatch(updateIsMyHomeExist(myHomeIsExist));
    await dispatch(loadUnreadMessages({ force: true }));
  } catch (e) {
    context.telemetry.increment('actions.refresh-state.fail');
    context.logger.warning(e, {
      domain: 'actions.headerState.refreshState',
    });
  } finally {
    if (latestRefreshIds.get(dispatch) === refreshId) {
      latestRefreshIds.delete(dispatch);
    }
  }
};
